// React

// @mui
import {
  Button,
  TextField,
  Autocomplete,
  Grid,
  ListItem,
  ListItemText,
} from '@mui/material'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'

// Redux

// Custom Hooks

// Custom Components
import DateRangePicker from '../DateRangePicker/DateRangePicker'

/**
 * Reusable GlobalFilterForm component
 * Props:
 * - filters: array of { title, value } objects for filter dropdown
 * - valueInputType: 'autocomplete' | 'text' | custom (default: 'text')
 * - valueOptions: array for autocomplete (if applicable)
 * - valueOptionLabel: function to get label from option (if applicable)
 * - valueOptionValue: function to get value from option (if applicable)
 * - valueInputProps: extra props for value input
 * - datePickerComponent: custom date picker component (default: DateRangePicker)
 * - onFilterChange, onValueChange, onDateChange: callbacks
 * - filter, value, startDate, endDate: controlled values
 * - loading, error: for autocomplete
 * - fetchButtonLabel, resetButtonLabel: button labels
 * - onFetch, onReset: callbacks for buttons
 */


import { useQueryState } from 'nuqs'

const GlobalFilterForm = ({
  filters = [],
  loading = false,
  error = null,
  datePickerComponent: DatePicker = DateRangePicker,
  fetchButtonLabel = 'Fetch',
  resetButtonLabel = 'Reset',
  onFetch,
  onReset,
  showDateFilter = true,
}) => {
  const [filter, setFilter] = useQueryState('filter', {
    defaultValue: null,
    type: 'string',
  })
  const [value, setValue] = useQueryState('value', {
    defaultValue: '',
    type: 'string',
  })
  const [startDate, setStartDate] = useQueryState('startDate', {
    defaultValue: null,
    type: 'date',
  })
  const [endDate, setEndDate] = useQueryState('endDate', {
    defaultValue: null,
    type: 'date',
  })

  // Handlers
  const handleFilterChange = (filterValue) => {
    setFilter(filterValue || null)
    // Reset value if filter changes
    if (filter !== filterValue) {
      setValue('')
    }
  }

  const handleValueChange = (val) => {
    setValue(val)
  }

  const handleDateChange = (newValue) => {
    setStartDate(newValue[0])
    setEndDate(newValue[1])
  }

  const handleFetch = () => {
    onFetch?.({ filter, value, startDate, endDate })
  }

  const handleReset = () => {
    setFilter(null)
    setValue('')
    setStartDate(null)
    setEndDate(null)
    onReset?.({ filter: null, value: '', startDate: null, endDate: null })
  }


  // Determine value input config from selected filter
  const selectedFilterObj = filters.find((f) => f.value === filter) || {}
  const valueInputType = selectedFilterObj.valueInputType || 'text'
  const valueOptions = selectedFilterObj.valueOptions || []
  const valueOptionLabel = selectedFilterObj.valueOptionLabel || ((opt) => opt?.label || '')
  const valueOptionValue = selectedFilterObj.valueOptionValue || ((opt) => opt?.value || '')
  const valueInputProps = selectedFilterObj.valueInputProps || {}

  return (
    <Grid
      container
      spacing={2}
      alignItems="center"
      sx={{
        backgroundColor: (theme) => theme.palette.background.paper,
        padding: (theme) =>  `${theme.spacing(1)} ${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(1)}`,
        boxShadow: (theme) => theme.shadows[1],
        width: '100%',
        margin: (theme) => `0 auto ${theme.spacing(3)}`,
        borderRadius: 2,
      }}
    >
      {/* Filter Dropdown */}
      <Grid item xs={12} sm={6} md={3}>
        <Autocomplete
          sx={{ '& input': { height: '28px' } }}
          fullWidth
          disablePortal
          size="small"
          id="filter-dropdown"
          options={filters}
          getOptionLabel={(option) => option.title}
          value={filters.find((f) => f.value === filter) || null}
          onChange={(event, newValue) => handleFilterChange(newValue?.value)}
          renderInput={(params) => <TextField {...params} label="Search By" />}
        />
      </Grid>

      {/* Value Input */}
      <Grid item xs={12} sm={6} md={3}>
        {valueInputType === 'autocomplete' ? (
          <Autocomplete
            sx={{ '& input': { height: '28px' } }}
            fullWidth
            disablePortal
            size="small"
            id="value-autocomplete"
            loading={loading}
            options={valueOptions}
            noOptionsText={
              error
                ? `Error: ${error?.message || 'Unknown error'}`
                : 'No options available'
            }
            value={
              valueOptions?.find((opt) => valueOptionValue(opt) === value) ||
              null
            }
            getOptionLabel={valueOptionLabel}
            renderOption={(props, option) => (
              <ListItem {...props} key={valueOptionValue(option)}>
                <ListItemText
                  primary={valueOptionLabel(option)}
                  secondary={valueOptionValue(option)}
                />
              </ListItem>
            )}
            onChange={(event, newValue) =>
              handleValueChange(valueOptionValue(newValue) || '')
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={`Select ${
                  selectedFilterObj.title || 'Value'
                }`}
                {...valueInputProps}
              />
            )}
          />
        ) : (
          <TextField
            sx={{ '& input': { height: '28px' } }}
            id="query-text"
            label={`Enter ${
              selectedFilterObj.title || 'Query'
            }`}
            variant="outlined"
            fullWidth
            size="small"
            value={value}
            onChange={(event) => handleValueChange(event.target.value)}
            disabled={!filter}
            {...valueInputProps}
          />
        )}
      </Grid>

      {/* Date Range Picker (optional) */}
      {showDateFilter && (
        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            key={`${startDate}-${endDate}`}
            onChange={handleDateChange}
            value={[startDate, endDate]}
          />
        </Grid>
      )}

      {/* Actions: Fetch & Reset */}
      <Grid item xs={12} sm={6} md={3} container spacing={2} justifyContent={showDateFilter ? 'flex-start' : 'flex-end'} alignItems="center">
        <Grid item xs={12} sm={6}>
          <Button
            style={{ whiteSpace: 'nowrap' }}
            variant="contained"
            startIcon={<SearchOutlinedIcon />}
            onClick={handleFetch}
            size="large"
            fullWidth
            sx={{ minWidth: {
              lg: 'fit-content',
            } }}
          >
            {fetchButtonLabel}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            style={{ whiteSpace: 'nowrap' }}
            variant="outlined"
            onClick={handleReset}
            size="large"
            fullWidth
            sx={{ minWidth: {
              lg: 'fit-content',
            } }}
          >
            {resetButtonLabel}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default GlobalFilterForm
